import React, { useState, useEffect, useCallback } from "react";
import SiraguLogo from "./SiraguLogo";
import { authHeaders, getUser, getToken, IDENTITY_HUB_API } from "../auth";
import "./Sidebar.css";

/* ── Chat sidebar ─────────────────────────────────────────────────────────
   Holds the "+ New Chat" button and the per-user history list. History is
   fetched from the backend; until Identity Hub login lands it will come back
   empty, so we show a friendly empty state rather than an error.
   ───────────────────────────────────────────────────────────────────────── */
function Sidebar({
  apiBase,
  open,
  collapsed,
  onToggleCollapse,
  onClose,
  onNewChat,
  onOpenSession,
  activeSessionId,
  refreshKey,
}) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  // Siragu's own brand icon — sourced live from Identity Hub's App Configuration
  // (the same `/auth/users/{id}/apps` mapping AppHeader's waffle switcher already
  // fetches), matched by app_url origin so it always reflects whatever icon is
  // configured there instead of a bundled SVG that can go stale. Cached in
  // localStorage for an instant first paint; falls back to the inline SiraguLogo
  // mark when nothing has resolved yet (or the fetch fails).
  const [appIcon, setAppIcon] = useState(() => {
    try {
      return localStorage.getItem("siragu_app_icon") || null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const user = getUser();
    if (!user || !user.id || !getToken()) return;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(
          `${IDENTITY_HUB_API}/auth/users/${user.id}/apps`,
          { headers: { ...authHeaders() } }
        );
        if (!res.ok) return;
        const data = await res.json();
        const mappings = (data && data.payload && data.payload.mappings) || [];
        const origin = window.location.origin;
        const self = mappings.find((m) => {
          if (!m) return false;
          const resolvedUrl = (m.app_url || "").replace(
            "[appHost]",
            window.location.hostname
          );
          try {
            return new URL(resolvedUrl).origin === origin;
          } catch {
            return false;
          }
        });
        const icon = self && self.app_icon;
        if (alive && icon && String(icon).startsWith("http")) {
          setAppIcon(icon);
          try {
            localStorage.setItem("siragu_app_icon", icon);
          } catch {
            /* ignore storage errors */
          }
        }
      } catch {
        // Keep whatever we had cached (or the SiraguLogo fallback).
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const loadSessions = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const res = await fetch(`${apiBase}/chat/sessions`, {
        headers: { ...authHeaders() },
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      setSessions(Array.isArray(data.sessions) ? data.sessions : []);
    } catch {
      // No auth yet / endpoint unavailable — treat as "empty" not "broken".
      setSessions([]);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions, refreshKey]);

  const formatWhen = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  return (
    <>
      <aside className={`sidebar${open ? " open" : ""}${collapsed ? " collapsed" : ""}`}>
        <div className="sidebar-brand">
          {appIcon ? (
            <img
              src={appIcon}
              alt="Siragu"
              className="sidebar-brand-icon"
              style={{ width: 24, height: 24 }}
            />
          ) : (
            <SiraguLogo size={24} />
          )}
          <span className="sidebar-brand-name">Siragu</span>
          {/* Collapse/expand toggle — desktop only (hidden on mobile via CSS). */}
          <button
            className="sidebar-collapse-btn"
            onClick={onToggleCollapse}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand" : "Collapse"}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d={collapsed ? "M9 6l6 6-6 6" : "M15 6l-6 6 6 6"}
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            className="sidebar-close"
            onClick={onClose}
            aria-label="Close sidebar"
            title="Close"
          >
            ×
          </button>
        </div>

        <button className="new-chat-btn" onClick={onNewChat}
          title={collapsed ? "New Chat" : undefined}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
          </svg>
          <span className="new-chat-label">New Chat</span>
        </button>

        <div className="sidebar-section-label">History</div>

        <div className="sidebar-history">
          {loading ? (
            <div className="sidebar-empty">Loading…</div>
          ) : sessions.length > 0 ? (
            sessions.map((s) => (
              <button
                key={s.id}
                className={`history-item${s.id === activeSessionId ? " active" : ""}`}
                onClick={() => onOpenSession(s.id)}
                title={s.title || "Untitled chat"}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="history-icon">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z"
                    stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                </svg>
                <span className="history-title">{s.title || "Untitled chat"}</span>
                <span className="history-when">{formatWhen(s.updated_at || s.created_at)}</span>
              </button>
            ))
          ) : (
            <div className="sidebar-empty">
              <div className="sidebar-empty-icon" aria-hidden="true">
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z"
                    stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                </svg>
              </div>
              <p className="sidebar-empty-text">
                Your past chats will appear here once you're signed in.
              </p>
              {failed && (
                <button className="sidebar-retry" onClick={loadSessions}>
                  Refresh
                </button>
              )}
            </div>
          )}
        </div>
      </aside>

      {open && <div className="sidebar-overlay" onClick={onClose} aria-hidden="true" />}
    </>
  );
}

export default Sidebar;
